import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ConsoleAuthContext = createContext(null);

export const ConsoleAuthProvider = ({ children }) => {
  const [admin, setAdmin]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [needsPin, setNeedsPin]     = useState(false); // PIN screen required
  const [hasPin, setHasPin]         = useState(false); // admin has configured a PIN

  const checkPinVerified = () => sessionStorage.getItem("console_pin_verified") === "1";

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(async ({ data }) => {
        if (data.role === "admin") {
          setAdmin(data);
          // Check if admin has a console PIN configured
          try {
            const ps = await axios.get(`${API}/admin/console/pin-status`, { withCredentials: true });
            const pinConfigured = ps.data.has_pin;
            setHasPin(pinConfigured);
            // If PIN configured and not yet verified this session
            if (pinConfigured && !checkPinVerified()) {
              setNeedsPin(true);
            }
          } catch {}
        } else {
          setAdmin(null);
        }
      })
      .catch(() => setAdmin(null))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (data.role !== "admin") {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        return { success: false, error: "Access denied. Admin credentials required." };
      }
      setAdmin(data);
      // Check PIN requirement
      try {
        const ps = await axios.get(`${API}/admin/console/pin-status`, { withCredentials: true });
        const pinConfigured = ps.data.has_pin;
        setHasPin(pinConfigured);
        if (pinConfigured && !checkPinVerified()) {
          setNeedsPin(true);
          return { success: true, needsPin: true };
        }
      } catch {}
      return { success: true, needsPin: false };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || "Login failed" };
    }
  };

  const pinVerified = () => {
    sessionStorage.setItem("console_pin_verified", "1");
    setNeedsPin(false);
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    sessionStorage.removeItem("console_pin_verified");
    setAdmin(null);
    setNeedsPin(false);
  };

  return (
    <ConsoleAuthContext.Provider value={{ admin, loading, needsPin, hasPin, login, logout, pinVerified }}>
      {children}
    </ConsoleAuthContext.Provider>
  );
};

export const useConsoleAuth = () => {
  const ctx = useContext(ConsoleAuthContext);
  if (!ctx) throw new Error("useConsoleAuth must be used inside ConsoleAuthProvider");
  return ctx;
};
